import { useGetLecture } from '#api/lectures';
import { Modal } from '#molecules';
import { useToast } from '#providers/ToastProvider';
import { produce } from 'immer';
import { useState } from 'react';
import styled from 'styled-components';
import { useDashboardCustomerMembershipState } from '../../dahsboardCustomerMembershipState';

interface DiscountModalProps {
  onClose: () => void;
}

export const DiscountModal = ({ onClose }: DiscountModalProps) => {
  const {
    dashboardCustomerMembershipState,
    setDashboardCustomerMembershipState
  } = useDashboardCustomerMembershipState();

  const { data } = useGetLecture(dashboardCustomerMembershipState.lectureId);

  const [state, setState] = useState({
    discount: dashboardCustomerMembershipState.discount as {
      unit: 'percent' | 'won';
      value: number | string | null;
    } | null
  });

  const { showToast } = useToast();

  const discount = state.discount;

  const disabled = discount === null;

  return (
    <Modal onClose={onClose}>
      <div className="w-full h-full p-4 bg-[#1e1a24] rounded-[20px] justify-start items-center gap-2.5 inline-flex">
        <div className="grow shrink basis-0 flex-col justify-center items-center gap-28 inline-flex">
          <div className="self-stretch  flex-col justify-start items-start gap-12 flex">
            <div className="flex-col justify-center items-start gap-1 flex">
              <div className="text-[#bcb8c3] text-xl font-semibold font-['Pretendard Variable'] leading-normal tracking-tight">
                할인
              </div>
            </div>
            <div className="self-stretch  flex-col justify-start items-start gap-20 flex">
              <div className="self-stretch flex-col justify-start items-start gap-12 flex">
                <div className="self-stretch justify-start items-center gap-8.5 inline-flex">
                  <div className="text-[#bcb8c3] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                    할인 여부
                  </div>
                </div>
                <div className="self-stretch justify-start items-start gap-8 inline-flex">
                  <Button
                    onClick={() => {
                      setState(prev => ({
                        ...prev,
                        discount: null
                      }));
                    }}
                    selected={discount === null}
                  >
                    없음
                  </Button>
                  <Button
                    onClick={() => {
                      setState(prev => ({
                        ...prev,
                        discount: {
                          unit: 'percent',
                          value: null
                        }
                      }));
                    }}
                    selected={discount !== null}
                  >
                    있음
                  </Button>
                </div>
              </div>
              <div className="self-stretch flex-col justify-start items-start gap-12 flex">
                <div className="self-stretch justify-start items-center gap-2.5 inline-flex">
                  <div className="text-[#bcb8c3] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                    할인 정도
                  </div>
                </div>
                <div className="self-stretch flex-col justify-start items-start gap-8 flex">
                  <div className="self-stretch justify-start items-start gap-8 inline-flex">
                    <Button
                      onClick={() => {
                        setState(
                          produce(draft => {
                            if (draft.discount !== null) {
                              draft.discount.unit = 'percent';
                            }
                          })
                        );
                      }}
                      disabled={disabled}
                      selected={
                        discount !== null && discount.unit === 'percent'
                      }
                    >
                      퍼센트 (%)
                    </Button>
                    <Button
                      onClick={() => {
                        setState(
                          produce(draft => {
                            if (draft.discount !== null) {
                              draft.discount.unit = 'won';
                            }
                          })
                        );
                      }}
                      disabled={disabled}
                      selected={discount !== null && discount.unit === 'won'}
                    >
                      원 (₩)
                    </Button>
                  </div>
                </div>
                <InputWrapper disabled={disabled}>
                  <Input
                    type="number"
                    pattern="[0-9]*"
                    min={0}
                    disabled={disabled}
                    placeholder="숫자를 입력해주세요"
                    value={discount?.value ?? ''}
                    onChange={e => {
                      const value = e.target.value;

                      // 빈 문자열로 변환하여 0을 포함한 값을 직접 처리
                      if (value === '' || value.endsWith('0')) {
                        setState(
                          produce(draft => {
                            if (draft.discount !== null) {
                              draft.discount.value = value;
                            }
                          })
                        );
                      } else {
                        const numberValue = Number(value);

                        setState(
                          produce(draft => {
                            if (draft.discount !== null) {
                              draft.discount.value = numberValue;
                            }
                          })
                        );
                      }
                    }}
                  />
                </InputWrapper>
              </div>
            </div>
          </div>
          <div className="self-stretch justify-center items-center gap-12 inline-flex">
            <div
              onClick={onClose}
              className="grow shrink basis-0 h-[45px] px-10 py-3 bg-[#2e2a3a] rounded-lg justify-center items-center gap-2.5 flex"
            >
              <div className="text-[#bcb8c3] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                취소
              </div>
            </div>
            <div
              onClick={() => {
                const numberValue = Number(discount?.value);

                if (numberValue < 0) {
                  showToast({
                    type: 'fail',
                    message: '할인은 0 이상의 숫자만 입력 가능합니다.'
                  });
                  return;
                }

                const max = discount?.unit === 'percent' ? 100 : data?.price;

                if (data && max !== undefined && numberValue > max) {
                  showToast({
                    type: 'fail',
                    message: `수강권의 가격은 ${max}원입니다.`
                  });
                  return;
                }

                setDashboardCustomerMembershipState(
                  produce(draft => {
                    draft.discount = state.discount;
                  })
                );
                onClose();
              }}
              className="grow shrink basis-0 h-[45px] px-10 py-3 bg-[#7750e9] rounded-lg justify-center items-center gap-2.5 flex"
            >
              <div className="text-[#ebe9f4] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                확인
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const InputWrapper = styled.div<{ disabled: boolean }>`
  border-radius: 8px;
  border: 1px solid var(--BG-bg-500, #373245);
  background-color: ${({ disabled }) =>
    disabled ? 'var(--Gray-Scale-600, #756F7E)' : 'transparent'};

  display: flex;
  height: 53px;
  padding: 16px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const Input = styled.input`
  appearance: none;
  background-color: transparent;
  width: 100%;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--Gray-Scale-700, #595463);
    /* NEW/body_02_regular */
    font-family: 'Pretendard Variable';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px; /* 131.25% */
    letter-spacing: -0.32px;
  }
`;

const Button = styled.button<{
  selected: boolean;
  disabled?: boolean;
}>`
  display: flex;
  padding: 12px 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;

  border-radius: 8px;
  background: ${({ disabled = false }) =>
    disabled ? 'var(--Gray-Scale-600, #756F7E)' : 'var(--BG-bg-600, #2e2a3a)'};
  border: 1px solid
    ${({ selected }) =>
      selected ? 'var(--Brand-400, #7750E9)' : 'var(--BG-bg-600, #2e2a3a)'};
`;
